import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/Button";

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const addDays = (d: Date, days: number) => {
  const t = new Date(d);
  t.setDate(t.getDate() + days);
  return t;
};

const startOfWeek = (d: Date, weekStartsOnMonday = true) => {
  const day = d.getDay();
  const diff = weekStartsOnMonday ? (day + 6) % 7 : day;
  return addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -diff);
};

const endOfWeek = (d: Date, weekStartsOnMonday = true) => {
  return addDays(startOfWeek(d, weekStartsOnMonday), 6);
};

interface WeekPickerProps {
  initialDate?: Date;
  weekStartsOnMonday?: boolean;
  onClose?: () => void;
  onSelectWeek: (start: Date, end: Date) => void;
}

const WeekPicker: React.FC<WeekPickerProps> = ({
  initialDate = new Date(),
  weekStartsOnMonday = true,
  onSelectWeek,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );
  const [selectedWeekStart, setSelectedWeekStart] = React.useState<Date>(
    startOfWeek(initialDate, weekStartsOnMonday)
  );
  const [hoverWeekStart, setHoverWeekStart] = React.useState<Date | null>(null);

  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const endOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  );
  const prevMonthEnd = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    0
  );

  const days: { date: Date; currentMonth: boolean }[] = [];

  for (let i = startOfMonth.getDay() - 1; i >= 0; i--) {
    const d = new Date(prevMonthEnd);
    d.setDate(prevMonthEnd.getDate() - i);
    days.push({ date: d, currentMonth: false });
  }

  for (let i = 1; i <= endOfMonth.getDate(); i++) {
    days.push({
      date: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i),
      currentMonth: true,
    });
  }
  while (days.length % 7 !== 0) {
    const nextDay = new Date(endOfMonth);
    nextDay.setDate(endOfMonth.getDate() + (days.length % 7));
    days.push({ date: nextDay, currentMonth: false });
  }

  const changeMonth = (offset: number) =>
    setCurrentMonth((s) => new Date(s.getFullYear(), s.getMonth() + offset, 1));
  const changeYear = (offset: number) =>
    setCurrentMonth((s) => new Date(s.getFullYear() + offset, s.getMonth(), 1));

  const handlePick = (d: Date) => {
    const s = startOfWeek(d, weekStartsOnMonday);
    const e = endOfWeek(d, weekStartsOnMonday);
    setSelectedWeekStart(s);
    onSelectWeek(s, e);
    if (onClose) onClose();
  };

  const isSameDate = (d1: Date, d2: Date) =>
    d1.toDateString() === d2.toDateString();

  return (
    <div className="w-[380px] p-4 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 border-purple-300 text-[#885ABB] flex items-center justify-center"
            onClick={() => changeYear(-1)}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={4} />
            <ChevronLeft className="h-4 w-4 -ml-4" strokeWidth={4} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 border-purple-300 text-[#885ABB] flex items-center justify-center"
            onClick={() => changeMonth(-1)}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={4} />
          </Button>
        </div>

        <h2 className="text-[16px] font-normal text-black">
          {currentMonth.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </h2>

        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 border-purple-300 text-[#885ABB] flex items-center justify-center"
            onClick={() => changeMonth(1)}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={4} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 border-purple-300 text-[#885ABB] flex items-center justify-center"
            onClick={() => changeYear(1)}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={4} />
            <ChevronRight className="h-4 w-4 -ml-4" strokeWidth={4} />
          </Button>
        </div>
      </div>

      {/* Days of Week */}
      <div className="grid grid-cols-7 text-xs font-light text-[#885ABB] mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="text-center">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-2 text-center">
        {days.map(({ date, currentMonth: isCurrentMonth }) => {
          const weekStart = startOfWeek(date, weekStartsOnMonday);
          const isSelected =
            selectedWeekStart &&
            weekStart.toDateString() === selectedWeekStart.toDateString();
          const isHovered =
            hoverWeekStart &&
            weekStart.toDateString() === hoverWeekStart.toDateString();

          return (
            <button
              key={date.toISOString()}
              onClick={() => handlePick(date)}
              onMouseEnter={() => setHoverWeekStart(weekStart)}
              onMouseLeave={() => setHoverWeekStart(null)}
              className={`h-9 w-9 mx-auto flex items-center justify-center rounded-full text-sm
                ${
                  !isCurrentMonth
                    ? "text-gray-300"
                    : !isSelected
                    ? "text-gray-800 hover:bg-purple-50"
                    : ""
                }`}
              style={{
                background: isSelected
                  ? "#885ABB"
                  : isHovered
                  ? "#E5D6FA"
                  : undefined,
                color: isSelected ? "#fff" : undefined,
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      {/* <div className="mt-4 flex ml-4 justify-start">
        <Button
          variant="outline"
          size="sm"
          className="w-[110px] h-9 border-purple-300 text-gray-800 font-poppins font-normal text-[15px]"
          // onClick={handleTodayClick}
        >
          Today
        </Button>
      </div> */}
    </div>
  );
};

export default WeekPicker;
